import React, { useEffect, useState, Fragment } from "react";
import { useSelector } from "react-redux";
import {
  createUserInCompany,
  updateUserRole,
  deleteUser,
} from "../services/companyAdminService";
import { ref, onValue } from "firebase/database";
import { db } from "../config/firebase";
import { useForm } from "react-hook-form";
import BoxWrapper from "./BoxWrapper";
import DialogModal from "../reusables/DialogModal";
import Loader from "./shared/Loader";
import { FaTrash, FaEdit } from "react-icons/fa";
import { PiSpinnerGapLight } from "react-icons/pi";
import { Toaster, toast } from 'react-hot-toast';
import { Listbox, Transition } from '@headlessui/react';
import { MdCheck } from "react-icons/md";
import { HiChevronUpDown } from "react-icons/hi2";
import SuspendedAccount from "../reusables/SuspendedAccount";

const AdminDashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const companyId = user?.companyId;
  const [companyInfo, setCompanyInfo] = useState({ name: "", plan: "", maxUsers: 0 });
  const [isSuspended, setIsSuspended] = useState(false);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxUsers, setMaxUsers] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const {
    register: editRegister,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
  } = useForm();

  const roles = ["user", "admin"];
  const [selectedRole, setSelectedRole] = useState("");
  useEffect(() => {
    setValue("role", selectedRole);
  }, [selectedRole, setValue]);

  useEffect(() => {
    if (!companyId) return;

    onValue(ref(db, `companies/${companyId}/users`), (snapshot) => {
      const data = snapshot.val();
      setUsers(data ? Object.entries(data).map(([uid, val]) => ({ uid, ...val })) : []);
    });

    onValue(ref(db, `companies/${companyId}`), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCompanyInfo({
          name: data.name || "",
          plan: data.plan || "",
          maxUsers: data.maxUsers || 0
        });
        setIsSuspended(!!data.suspended);
      }
    });

    onValue(ref(db, `companies/${companyId}/maxUsers`), (snapshot) => {
      setMaxUsers(snapshot.val());
    });
  }, [companyId]);

  const onSubmit = async (data) => {
    if (maxUsers && users.length >= maxUsers) {
      toast.error("User limit reached. Upgrade your plan or remove a user.");
      return;
    }

    setLoading(true);
    try {
      await createUserInCompany(data.email, data.password, data.role);
      reset();
    } catch (error) {
      toast.error("Failed to create user: " + error.message);
    } finally {
      setLoading(false);
      toast.success('User created!');
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setLoading(true);
    try {
      setDeleteDialogOpen(false);
      await deleteUser(userToDelete);
    } catch (err) {
      toast.error("Failed to delete user: " + err.message);
    } finally {
      setLoading(false);
      toast.success('User deleted!');
    }
  };

  const handleEditRole = async (data) => {
    if (!userToEdit) return;
    setLoading(true);
    try {
      await updateUserRole(companyId, userToEdit.uid, data.newRole);
      setEditDialogOpen(false);
    } catch (err) {
      toast.error("Failed to update user role: " + err.message);
    } finally {
      setLoading(false);
      toast.success('User role updated!');
    }
  };

  if (isSuspended) {
    return (
      <SuspendedAccount />
    );
  }

  return (
    <div className="w-full p-6">
      <Toaster position="top-center"/>
      <div className="mb-6">
        <BoxWrapper title="Company Details">
          <div className="flex flex-row justify-between items-center pt-4">
            <span className="text-gray-600">Name: <span className="font-bold text-xl">{companyInfo.name}</span></span>
            <span className="text-gray-600">Plan: <span className="font-bold text-xl">{companyInfo.plan}</span></span>
            <span className="text-gray-600">Remaining User Slots: <span className="font-bold text-xl">{Math.max(companyInfo.maxUsers - users.length, 0)}</span>/{companyInfo.maxUsers}</span>
          </div>
        </BoxWrapper>
      </div>
      {loading && <Loader />}

      {/* Delete Dialog */}
      <DialogModal isOpen={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} title="Delete User">
        <p className="text-sm text-gray-500">Are you sure you want to delete this user?</p>
        <div className="mt-6 flex justify-between gap-3">
          <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setDeleteDialogOpen(false)}>Cancel</button>
          <button className="px-4 py-2 bg-red-600 text-white rounded" onClick={handleDelete}>Confirm</button>
        </div>
      </DialogModal>

      {/* Edit Role Dialog */}
      <DialogModal isOpen={editDialogOpen} onClose={() => setEditDialogOpen(false)} title="Edit User Role">
        <form onSubmit={handleEditSubmit(handleEditRole)} className="grid gap-3 mt-4">
          <select {...editRegister("newRole", { required: true })} className="p-3 border rounded-md">
            <option value="">Select new role</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <div className="flex justify-end gap-3">
            <button type="button" className="px-4 py-2 bg-gray-300 rounded" onClick={() => setEditDialogOpen(false)}>Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
          </div>
        </form>
      </DialogModal>

      {/* Users List */}
      <BoxWrapper title="Users">
        <table className="border-collapse w-full mt-4">
          <thead>
            <tr>
              {/* <th className="bg-customeBlack text-white border-white border">UID</th> */}
              <th className="bg-customeBlack text-white border-white border">Email</th>
              <th className="bg-customeBlack text-white border-white border">Role</th>
              <th className="bg-customeBlack text-white border-white border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.uid}>
                {/* <td className="border p-2">{u.uid}</td> */}
                <td className="border p-2">{u.email}</td>
                <td className="border p-2">{u.role}</td>
                <td className="border p-2 flex gap-2">
                  <button
                    onClick={() => {
                      setUserToEdit(u);
                      resetEdit({ newRole: u.role });
                      setEditDialogOpen(true);
                    }}
                    className="text-white bg-blue-600 px-2 py-1 rounded"
                  >
                    <FaEdit />
                  </button>
                  {u.uid !== user.id && (
                    <button
                      onClick={() => {
                        setUserToDelete(u.uid);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-white bg-red-600 px-2 py-1 rounded"
                    >
                      <FaTrash />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </BoxWrapper>

      {/* Create New User Form */}
      <div className="flex gap-6 py-6">
        <div className="w-2/4">
          <BoxWrapper title="Create New User">
            <form onSubmit={handleSubmit(onSubmit)} className="mt-4 grid gap-3">
              <input
                placeholder="Email"
                type="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: "Invalid email format",
                  },
                })}
                className="p-3 rounded-md border-2 text-gray-700"
              />
              {errors.email && <span className="text-red-500 text-sm">{errors.email.message}</span>}

              <input
                placeholder="Password"
                type="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: { value: 6, message: "Minimum 6 characters" },
                  validate: (val) => typeof val === "string" || "Invalid password type",
                })}
                className="p-3 rounded-md border-2 text-gray-700"
              />
              {errors.password && <span className="text-red-500 text-sm">{errors.password.message}</span>}

              <Listbox value={selectedRole} onChange={setSelectedRole}>
                <div className="relative mt-1">
                  <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left border-2 text-gray-700 shadow-sm focus:outline-cyan-500">
                    <span className="block truncate">{selectedRole || "Select a role"}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <HiChevronUpDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-50">
                      {roles.map((role, idx) => (
                        <Listbox.Option
                          key={idx}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                            }`
                          }
                          value={role}
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {role}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                  <MdCheck className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
              {errors.role && <span className="text-red-500 text-sm">{errors.role.message}</span>}

              <button
                type="submit"
                className="bg-customeBlack text-white py-3 rounded flex items-center justify-center mt-4"
                disabled={loading}
              >
                {loading && <PiSpinnerGapLight className="animate-spin h-4 w-4 text-white mr-2" />}
                <span>{loading ? "Creating..." : "Create User"}</span>
              </button>
            </form>
          </BoxWrapper>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;